import { authenticatedApiClient } from './axios'
import {
  PERK_ENDPOINT,
  SAVED_PERK_ENDPOINT,
  REFERRAL_OFFER_ENDPOINT,
  GET_REFERRAL_LINK_ENDPOINT,
  REFERRAL_USER_SETTINGS_ENDPOINT,
  GET_REFERRAL_STATS_ENDPOINT,
} from './config/api'
import type {
  PerkData,
  ReferralOffer,
  ReferralStats,
  ReferralUserSettings,
  UpdateReferralSettingsRequest,
  SavePerkRequest,
  UnsavePerkRequest,
  RedeemPerkRequest,
} from '@cobu/types/perk'
import { AxiosError } from 'axios'

export async function getPerks(userUuid: string): Promise<PerkData[]> {
  return await authenticatedApiClient.get<PerkData[]>(PERK_ENDPOINT, {
    params: { userForSavedPerk: userUuid, showCount: true },
  })
}

export async function getSavedPerks(userUuid: string): Promise<PerkData[]> {
  return await authenticatedApiClient.get<PerkData[]>(SAVED_PERK_ENDPOINT, {
    params: { user: userUuid, showCount: true },
  })
}

export async function getPerk(perkUuid: string): Promise<PerkData> {
  return await authenticatedApiClient.get<PerkData>(`${PERK_ENDPOINT}/${perkUuid}`, {
    params: { showCount: true },
  })
}

export async function savePerk(request: SavePerkRequest): Promise<PerkData> {
  return await authenticatedApiClient.post<PerkData>(SAVED_PERK_ENDPOINT, request, {})
}

export async function unsavePerk(request: UnsavePerkRequest): Promise<void> {
  await authenticatedApiClient.delete(`${SAVED_PERK_ENDPOINT}/${request.savedPerkId}`)
}

export async function redeemPerk(request: RedeemPerkRequest): Promise<void> {
  await authenticatedApiClient.post(`${PERK_ENDPOINT}/${request.perk}/redeem`, request, {})
}

// Backend answers '' when the building has no live offer.
export async function getReferralOffer(buildingId: string): Promise<ReferralOffer | null> {
  const offer = await authenticatedApiClient.get<ReferralOffer | ''>(REFERRAL_OFFER_ENDPOINT, { params: { buildingId } })
  return offer || null
}

export async function getReferralLink(buildingId: string, userId: string): Promise<string> {
  return await authenticatedApiClient.get<string>(GET_REFERRAL_LINK_ENDPOINT, { params: { buildingId, userId } })
}

export async function getReferralStats(): Promise<ReferralStats> {
  return await authenticatedApiClient.get<ReferralStats>(GET_REFERRAL_STATS_ENDPOINT)
}

export async function getReferralUserSettings(): Promise<ReferralUserSettings> {
  try {
    return await authenticatedApiClient.get<ReferralUserSettings>(REFERRAL_USER_SETTINGS_ENDPOINT)
  } catch (error) {
    console.error('Error getting referral user settings', error)
    if (error instanceof AxiosError && error.response?.status === 404) {
      return {
        uuid: '',
        user: '',
        welcome_message: '',
        details: { includeProfileImage: false },
      }
    }
    throw error
  }
}

export async function updateReferralUserSettings(request: UpdateReferralSettingsRequest): Promise<ReferralUserSettings> {
  return await authenticatedApiClient.patch<ReferralUserSettings>(REFERRAL_USER_SETTINGS_ENDPOINT, request)
}

export async function getBuildingRedemptions(perkUuid: string, buildingUuid: string): Promise<any> {
  return await authenticatedApiClient.get(`${PERK_ENDPOINT}/${perkUuid}/redemptions`, { params: { building: buildingUuid } })
}


